<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.gamification_items', 2),
                        href: meta.links.items_index,
                    },
                    {
                        name: $tc('messages.gamification_trades', 2),
                        href: meta.links.trades_index,
                    },
                ]"
            />
        </template>

        <div class="md:grid md:grid-cols-4 md:gap-6">
            <div class="col-span-1">
                <div>
                    {{ trade.date_display }}
                </div>
                <div>
                    <div class="flex md:justify-center">
                        <IconArrowsLeftRight
                            class="w-16 h-16"
                            :class="{
                                'text-indigo-200': trade.status === null,
                                'text-green-200': trade.status === 'finished',
                                'text-yellow-200': trade.status === 'error',
                            }"
                        />
                    </div>
                </div>
                <div>
                    <BadgePill v-if="trade.status === null" color="indigo">
                        {{ $t('messages.created') }}
                    </BadgePill>
                    <BadgePill v-else-if="trade.status === 'finished'" color="green">
                        {{ $t('messages.completed') }}
                    </BadgePill>
                    <BadgePill v-else-if="trade.status === 'error'" color="yellow">
                        {{ $t('messages.error') }}:
                        {{ $t('messages.quantity') }}
                    </BadgePill>
                </div>
                <div class="mt-3 flex gap-x-2">
                    <Tippy>
                        <button>
                            <IconInfoCircle class="h-6 w-6 text-gray-400" />
                        </button>
                        <template #content>
                            {{ $t('messages.trade_explanation') }}
                        </template>
                    </Tippy>
                    <Tippy v-if="trade.balance.is_gift">
                        <button>
                            <IconGift class="h-6 w-6 text-gray-400" />
                        </button>
                        <template #content>
                            {{ $t('messages.trade_gift_explanation') }}
                        </template>
                    </Tippy>
                    <Tippy v-if="trade.balance.imbalance">
                        <button>
                            <IconScaleOff class="h-6 w-6 text-gray-400" />
                        </button>
                        <template #content>
                            {{ $t('messages.trade_is_imbalanced') }}
                        </template>
                    </Tippy>
                </div>
            </div>

            <div class="md:grid md:grid-cols-2 md:gap-6 col-span-3">
                <div>
                    <AppCard>
                        <div class="flex gap-x-2 md:gap-x-4">
                            <img
                                class="w-16 h-16 rounded-full"
                                :src="trade.first.avatar_or_profile_photo"
                                :title="trade.first.first_name_and_initial"
                                alt="Avatar"
                            />
                            <div>
                                <div class="mt-1 rounded-md shadow-sm" v-if="trade.settings.coins.first">
                                    <img
                                        class="h-6 w-6 mr-1"
                                        src="/assets/gamification/currency/coin.svg"
                                        alt="emerald"
                                    />
                                    <div>
                                        {{ trade.settings.coins.first }}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div>
                                    <TradeElementsDisplay
                                        side="first"
                                        :elements="items"
                                        :selected="trade.settings.items"
                                        element-class="h-12 w-12"
                                    />
                                </div>
                                <div class="mt-2">
                                    <TradeElementsDisplay
                                        side="first"
                                        :elements="cards"
                                        :selected="trade.settings.cards"
                                        element-class="h-12 w-12"
                                    />
                                </div>
                            </div>
                        </div>
                    </AppCard>
                    <AppCard class="mt-3">
                        <div class="flex gap-x-2 md:gap-x-4">
                            <img
                                class="w-16 h-16 rounded-full"
                                :src="trade.second.avatar_or_profile_photo"
                                :title="trade.second.first_name_and_initial"
                                alt="Avatar"
                            />
                            <div>
                                <div class="mt-1 rounded-md shadow-sm" v-if="trade.settings.coins.second">
                                    <img
                                        class="h-6 w-6 mr-1"
                                        src="/assets/gamification/currency/coin.svg"
                                        alt="emerald"
                                    />
                                    <div>
                                        {{ trade.settings.coins.second }}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div>
                                    <TradeElementsDisplay
                                        side="second"
                                        :elements="items"
                                        :selected="trade.settings.items"
                                        element-class="h-12 w-12"
                                    />
                                </div>
                                <div class="mt-2">
                                    <TradeElementsDisplay
                                        side="second"
                                        :elements="cards"
                                        :selected="trade.settings.cards"
                                        element-class="h-12 w-12"
                                    />
                                </div>
                            </div>
                        </div>
                    </AppCard>

                    <div v-if="trade.balance.imbalance" class="mt-3">
                        <ProgressBar :percent="trade.balance.percent" :show-text="false" color="gray" />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="trade.status === 'error'">
            <BadgePill color="yellow"> {{ $t('messages.error') }}: {{ $t('messages.quantity') }} </BadgePill>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import BadgePill from '@/components/BadgePill.vue';
import { IconArrowsLeftRight, IconGift, IconInfoCircle, IconScaleOff } from '@tabler/icons-vue';
import { Tippy } from 'vue-tippy';
import AppCard from '@/Shared/AppCard.vue';
import TradeElementsDisplay from '@/components/TradeElementsDisplay.vue';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';

export default {
    components: {
        ProgressBar,
        TradeElementsDisplay,
        AppCard,
        IconScaleOff,
        Tippy,
        IconArrowsLeftRight,
        IconGift,
        IconInfoCircle,
        PanelPage,
        BadgePill,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'items', 'cards', 'filters', 'gamificationSet', 'cardCollections', 'trade'],
    methods: {},
    computed: {
        givenItems: function () {
            let cards = this.cardCollections.flatMap((c) => c.cards).filter((i) => i[this.trade.current]);
            let items = this.items.filter((i) => i[this.trade.current]);
            return [...items, ...cards];
        },
        requestedItems: function () {
            let cards = this.cardCollections.flatMap((c) => c.cards).filter((i) => i[this.trade.other]);
            let items = this.items.filter((i) => i[this.trade.other]);
            return [...items, ...cards];
        },
    },
};
</script>

<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
